import { Suspense, useEffect, useMemo, useRef } from "react";
import { Outlet, useLocation, useSearchParams } from "react-router-dom";
import usePrevious from "react-use/lib/usePrevious";
import Navigation from "@/components/Navigation";
import Spinner from "@/components/Spinner";
import { useAuth } from "@/contexts/AuthContext";
import { useJournalFilterContext } from "@/contexts/JournalFilterContext";
import useMediaQuery from "@/hooks/useMediaQuery";
import { useSidebarCollapse } from "@/hooks/useSidebarCollapse";
import { cn } from "@/lib/utils";
import Landing from "@/pages/Landing";

// ❌ REMOVED: redirectOnAuthFailure import (v0.66.0 - no longer needed)

const RootLayout = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const sm = useMediaQuery("sm");
  const { currentUser, isInitialized } = useAuth(); // 获取当前用户状态
  // ❌ REMOVED: currentUser (v0.66.0 - no longer needed)
  const { removeFilter } = useJournalFilterContext();
  // ❌ REMOVED: memoRelatedSetting (v0.66.0 - no longer needed)
  const pathname = useMemo(() => location.pathname, [location.pathname]);
  const prevPathname = usePrevious(pathname);

  // Use shared sidebar state from context
  const { isCollapsed } = useSidebarCollapse();
  const sidebarRef = useRef<HTMLDivElement>(null);

  // ❌ REMOVED: useEffect for disallowPublicVisibility redirect (v0.66.0 - all memos are private)

  useEffect(() => {
    // When the route changes and there is no filter in the search params, remove all filters
    if (prevPathname !== pathname && !searchParams.has("filter")) {
      removeFilter(() => true);
    }
  }, [prevPathname, pathname, searchParams, removeFilter]);

  // 未登录且未初始化时显示加载状态
  if (!isInitialized) {
    return (
      <div className="w-full h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  // 未登录用户显示 Landing Page
  if (!currentUser) {
    return <Landing />;
  }

  // 已登录用户显示正常应用界面
  return (
    <div className={cn("w-full min-h-full flex flex-row justify-center items-start", isCollapsed ? "sm:pl-16" : "sm:pl-56")}>
      {sm && (
        <div
          ref={sidebarRef}
          className={cn(
            "flex flex-col justify-start items-start fixed top-0 left-0 select-none h-full bg-sidebar transition-all duration-300 z-50",
            isCollapsed ? "w-16 px-2" : "w-56 px-3",
            "border-r border-border",
          )}
        >
          <div className="relative w-full h-full flex flex-col">
            {/* Navigation */}
            <Navigation className="py-4 md:pt-6" collapsed={isCollapsed} />
          </div>
        </div>
      )}
      <main className="w-full h-auto grow shrink flex flex-col justify-start items-center">
        <Suspense
          fallback={
            <div className="w-full h-64 flex items-center justify-center">
              <Spinner size="lg" />
            </div>
          }
        >
          <Outlet />
        </Suspense>
      </main>
    </div>
  );
};

export default RootLayout;
